import { useState } from "react";

import { api, ApiError } from "../lib/api.ts";

/**
 * Small input + button on the per-agent page to put a task back into the
 * agent's queue after the owner edited it post-completion.
 *
 * Calls POST /api/agents/:key/tasks/:taskId/reopen which sets
 * agent_status="ready" on the task and posts an audit comment. The actual
 * re-claim happens on the agent's next poll (≤5s if the agent is online).
 */
export function ReopenTaskBox({ agentKey }: { agentKey: string }) {
	const [taskId, setTaskId] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [okMessage, setOkMessage] = useState<string | null>(null);

	async function handleSubmit() {
		const id = taskId.trim();
		if (!id) return;
		setBusy(true);
		setError(null);
		setOkMessage(null);
		try {
			await api.reopenAgentTask(agentKey, id);
			setOkMessage(`✓ Task ${id} → ready. Agent will re-claim within ~5s if online.`);
			setTaskId("");
		} catch (err) {
			if (err instanceof ApiError) setError(`${err.status} — ${err.message}`);
			else setError((err as Error).message);
		} finally {
			setBusy(false);
		}
	}

	return (
		<section className="mt-6 border border-zinc-800 rounded-lg bg-zinc-900/30">
			<header className="px-4 py-3 border-b border-zinc-800">
				<h2 className="text-sm font-semibold text-zinc-100">Reopen task</h2>
				<div className="text-[11px] text-zinc-500 mt-0.5">
					Edited a task after the agent finished it? Paste its id here to flip its{" "}
					<code>agent_status</code> back to <code>ready</code>. Make sure the title still has{" "}
					<code>[READY]</code> — otherwise the agent will release it again on claim.
				</div>
			</header>
			<div className="p-4 space-y-2">
				<div className="flex gap-2">
					<input
						type="text"
						value={taskId}
						onChange={(e) => setTaskId(e.target.value)}
						placeholder="task id (UUID or short key from tasks.g1.network)"
						className="flex-1 px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm font-mono placeholder:text-zinc-600 focus:outline-none focus:border-zinc-500"
						disabled={busy}
						onKeyDown={(e) => {
							if (e.key === "Enter") handleSubmit();
						}}
					/>
					<button
						type="button"
						onClick={handleSubmit}
						disabled={busy || taskId.trim().length === 0}
						className="px-3 py-1.5 bg-accent-600 hover:bg-accent-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-white text-sm font-medium rounded"
					>
						{busy ? "Sending…" : "Send to agent"}
					</button>
				</div>
				{okMessage && <div className="text-xs text-emerald-300">{okMessage}</div>}
				{error && <div className="text-xs text-red-300">{error}</div>}
			</div>
		</section>
	);
}
